import { readSessionError } from '../../common/services/device-session.service';

/** The codes the signup API answers with (docs/plg-free-plan.md). */
export const API_ERROR_CODES = [
    'EMAIL_DISPOSABLE',
    'EMAIL_UNDELIVERABLE',
    'EMAIL_TAKEN',
    'DOMAIN_TAKEN',
    'WEBSITE_INVALID',
    'HUMAN_CHECK_FAILED',
    'TOO_MANY',
    'PASSWORD_WEAK',
    'GOOGLE_TOKEN_INVALID',
    'DEMO_TIME_INVALID',
    'DEMO_SLOT_TAKEN',
] as const;

/** Plus the ones the page works out itself. */
export type SignupErrorCode =
    | (typeof API_ERROR_CODES)[number]
    | 'GOOGLE_UNAVAILABLE'
    | 'GOOGLE_FAILED'
    | 'NETWORK'
    | 'UNKNOWN';

/** Errors that mean "you already have an account": they come with a sign-in link. */
export const SIGN_IN_INSTEAD: SignupErrorCode[] = ['EMAIL_TAKEN', 'DOMAIN_TAKEN'];

/** The i18n key that says it in plain words. */
export const errorKey = (code: SignupErrorCode) => `SIGNUP.ERRORS.${code}`;

/**
 * What went wrong, as one of our codes. The API's `code` wins; otherwise the
 * HTTP status says enough (429 → too many, 501 → Google not configured), and
 * a request that never got an answer is the network.
 */
export async function readSignupError(e: any): Promise<SignupErrorCode> {
    const body: any = await readSessionError(e).catch(() => null);
    const code = typeof body?.code === 'string' ? body.code.toUpperCase() : '';
    if ((API_ERROR_CODES as readonly string[]).includes(code)) return code as SignupErrorCode;

    const status: number | undefined = e?.status;
    if (status === 429) return 'TOO_MANY';
    if (status === 501) return 'GOOGLE_UNAVAILABLE';
    if (!status) return 'NETWORK';
    const message = Array.isArray(body?.message) ? body.message.join(' ') : String(body?.message ?? '');
    if (status === 400 && /password/i.test(message)) return 'PASSWORD_WEAK';
    if (status === 400 && /website|domain|url/i.test(message)) return 'WEBSITE_INVALID';
    if (status === 400 && /email/i.test(message)) return 'EMAIL_UNDELIVERABLE';
    return 'UNKNOWN';
}
